import { useMemo } from "react"
import { isEmpty, isNil, mergeWith } from "lodash"
import { getValidFilters } from "components/add-filter/helpers"
import { computeUpdatedFilters, formatSecurityForFilter, mapFactorExposureNegativeBoolOperator } from "helpers/filters"
import type { SecuritySearchFilter } from "resources/filters"
import type { Filter } from "resources/filter-ons"

type SecuritiesClause = NonNullable<SecuritySearchFilter["securities"]>
type FilterValueNumber = Filter["values"][number]
interface ComputedFilter {
  filter: SecuritySearchFilter | null
}

function mergeSecuritiesClause(primary: SecuritiesClause, secondary: SecuritiesClause): SecuritiesClause {
  const inFilter = [...(primary.in ?? []), ...(secondary.in ?? [])]
  const notInFilter = [...(primary.notIn ?? []), ...(secondary.notIn ?? [])]
  return {
    ...primary,
    ...secondary,
    ...(!isEmpty(inFilter) ? { in: inFilter } : {}),
    ...(!isEmpty(notInFilter) ? { notIn: notInFilter } : {}),
  }
}

function isSecuritiesClause(x: SecuritiesClause) {
  return typeof x === "object" && x !== null && ("in" in x || "notIn" in x)
}

function mergeSecuritySearchCustomizer(primary: SecuritiesClause, secondary: SecuritiesClause, key: PropertyKey): SecuritiesClause | undefined {
  if (key !== "securities" || !isSecuritiesClause(primary) || !isSecuritiesClause(secondary)) return undefined
  return mergeSecuritiesClause(primary, secondary)
}

// Non-securities universe rows expose the selected id on `value`; securities rows are full instruments (then stripped via formatSecurityForFilter).
function normalizeUniverseFilterValue(filter: Filter, value: FilterValueNumber): FilterValueNumber {
  if (
    typeof value === "number"
    || isNil(value)
    || !(typeof value === "object" && value !== null)
  ) return value

  if (filter.filterOn?.type === "securities") {
    return formatSecurityForFilter(value, false)
  }

  if ("value" in value && value.value !== undefined) {
    return value.value
  }

  return value
}

export default function useSecuritySearchFilter(filters: Filter[], universeFilters: Filter[]): SecuritySearchFilter | null {
  return useMemo(() => {
    const validFilters = getValidFilters(filters)
    const validUniverse = universeFilters.filter(filter => filter.filterOn && filter.operator && filter.values.length > 0)
    const universeNormalized = validUniverse.map(filter => ({
      ...filter,
      values: filter.values.map(value => normalizeUniverseFilterValue(filter, value)),
    }))
    const { filter: main }: ComputedFilter = computeUpdatedFilters(validFilters)
    const { filter: universe }: ComputedFilter = computeUpdatedFilters(universeNormalized)
    if (main && !universe) {
      return mapFactorExposureNegativeBoolOperator(main)
    }

    if (universe && !main) {
      return mapFactorExposureNegativeBoolOperator(universe)
    }

    if (!main && !universe) {
      return null
    }

    const combined = mergeWith({}, main, universe, mergeSecuritySearchCustomizer)
    if (isEmpty(combined)) return null

    return mapFactorExposureNegativeBoolOperator(combined)
  }, [filters, universeFilters])
}
